import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { BareInput } from "components/DesignSystem/Input/Input";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import {
  useCreateMeetingRequestMutation,
  useGetMeetingRequestAIDraftMutation,
} from "store/apis/meetingRequest";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

const TEAM_CALENDLY_LINKS: Record<number, string> = {
  0: "https://calendly.com/inklehq/bookkeeping-team",
  1: "https://calendly.com/inklehq/30-minutes-chat-with-a-tax-expert",
  2: "https://calendly.com/inklehq/inkle-support",
  3: "https://calendly.com/inkle-incorporate/meeting",
};

const DEFAULT_CALENDLY_LINK = TEAM_CALENDLY_LINKS[0];

const TEAM_OPTIONS = [
  { label: "Bookkeeping", value: 0 },
  { label: "Tax", value: 1 },
  { label: "Support", value: 2 },
  { label: "Incorporation", value: 3 },
];

const INITIAL_STATE = {
  team: 0,
  calendlyLink: DEFAULT_CALENDLY_LINK,
  purpose: "",
  message: "",
  isDraftGenerated: false,
};

type ScheduleMeetingModalProps = ModalProps & {
  groupId: string;
  channelUrl: string;
};

export const ScheduleMeetingModal = ({
  isOpen,
  close,
  groupId,
  channelUrl,
}: ScheduleMeetingModalProps) => {
  const { alertToast, successToast } = useToast();

  const [team, setTeam] = useState(INITIAL_STATE.team);
  const [calendlyLink, setCalendlyLink] = useState(INITIAL_STATE.calendlyLink);
  const [purpose, setPurpose] = useState(INITIAL_STATE.purpose);
  const [message, setMessage] = useState(INITIAL_STATE.message);
  const [isDraftGenerated, setIsDraftGenerated] = useState(
    INITIAL_STATE.isDraftGenerated
  );

  const [createMeetingRequest, { isLoading: isSubmitting }] =
    useCreateMeetingRequestMutation();
  const [getAIDraft, { isLoading: isDrafting }] =
    useGetMeetingRequestAIDraftMutation();

  const comboboxOptions = useMemo(
    () =>
      TEAM_OPTIONS.map(({ label, value }) => ({ label, value: String(value) })),
    []
  );

  const selectedTeamOption = useMemo(() => {
    const found = TEAM_OPTIONS.find((o) => o.value === team);
    return found ? { label: found.label, value: String(found.value) } : null;
  }, [team]);

  const handleGenerateDraft = async () => {
    if (!calendlyLink) return;
    try {
      const { message: draft } = await getAIDraft({
        groupId,
        payload: {
          team,
          calendly_link: calendlyLink,
          purpose: purpose || undefined,
        },
      }).unwrap();
      setMessage(draft);
      setIsDraftGenerated(true);
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to generate draft",
      });
    }
  };

  const handleSend = async () => {
    try {
      await createMeetingRequest({
        groupId,
        payload: {
          team,
          channel_url: channelUrl,
          calendly_link: calendlyLink,
          final_message: message,
          purpose: purpose || undefined,
        },
      }).unwrap();
      successToast({ message: "Meeting request sent" });
      close();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to send meeting request",
      });
    }
  };

  const handleClose = () => {
    setTeam(INITIAL_STATE.team);
    setCalendlyLink(INITIAL_STATE.calendlyLink);
    setPurpose(INITIAL_STATE.purpose);
    setMessage(INITIAL_STATE.message);
    setIsDraftGenerated(INITIAL_STATE.isDraftGenerated);
    close();
  };

  const canSend = Boolean(calendlyLink && message) && !isSubmitting;

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Request a Meeting</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-flex t-flex-col t-gap-4">
            <Combobox
              label="Team"
              options={comboboxOptions}
              value={selectedTeamOption}
              onChange={(option) => {
                const teamValue = Number((option as OptionData)?.value);
                setTeam(teamValue);
                setCalendlyLink(TEAM_CALENDLY_LINKS[teamValue] ?? "");
              }}
            />
            <BareInput
              label="Calendly Link"
              value={calendlyLink}
              onChange={(e) => setCalendlyLink(e.target.value)}
              placeholder="https://calendly.com/..."
            />
            <BareInput
              label="Purpose (optional)"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. Review Q4 books"
            />
            <div className="t-flex t-flex-col t-gap-1.5">
              <div className="t-flex t-items-center t-justify-between">
                <label className="t-text-body-sm t-text-text-60">Message</label>
                <Button
                  type="button"
                  customType="ghost"
                  size="small"
                  onClick={handleGenerateDraft}
                  disabled={!calendlyLink || isDrafting}
                  isLoading={isDrafting}
                >
                  Generate with AI
                </Button>
              </div>
              <textarea
                className="t-w-full t-resize-none t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-p-3 t-text-body-sm t-text-text-100 focus:t-border-purple focus:t-bg-surface-transparent focus:t-outline-none"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your message or generate one with AI..."
                rows={5}
              />
              {isDraftGenerated && (
                <p className="t-text-body-sm t-text-text-30 t-m-0">
                  AI-generated draft — edit before sending if needed.
                </p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button">Cancel</Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={handleSend}
            disabled={!canSend}
            isLoading={isSubmitting}
          >
            Send
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
